import { useEffect, useState } from 'react'
import { SAArtwork } from 'types/Artwork'
import { SearchApi, UseReduxSearchProviderResponse, SortObject } from '../types'
import { isSafeRedirectUrl } from '../libs/helpers/url'
import useReduxSearchProvider from '../hooks/useReduxSearchProvider'

type UseSearchArtworksParams = {
  searchQuery: string
  selectedFilters: Record<string, ReadonlyArray<string>>
  searchService: SearchApi
  sort?: string
  page: number
  resultsPerPage: number
  destinationZone: string
  selectedCategory: string
  predefinedFilters?: Record<string, ReadonlyArray<string>>
  isPrintsPage?: boolean
  userSizePreference: string
  useReduxProviderHook?: () => UseReduxSearchProviderResponse
  defaultSortObject?: SortObject
}

type UseSearchArtworksReturn = {
  artworksResults: ReadonlyArray<SAArtwork>
  isLoadingArtworks: boolean
}

const useSearchArtworks = ({
  searchQuery,
  selectedFilters,
  searchService,
  sort,
  page,
  resultsPerPage,
  destinationZone,
  selectedCategory,
  predefinedFilters,
  isPrintsPage,
  userSizePreference,
  useReduxProviderHook = useReduxSearchProvider,
  defaultSortObject,
}: UseSearchArtworksParams): UseSearchArtworksReturn => {
  const [isLoadingArtworks, setIsLoadingArtworks] = useState<boolean>(false)
  // getting artworks from redux
  const { artworksResults, updateReduxStore } = useReduxProviderHook()

  const getSearchArtworksFromProvider = async () => {
    setIsLoadingArtworks(true)
    const hasSearchQuery = Boolean(searchQuery)
    const isSpecificCategorySelected = Boolean(selectedCategory) && selectedCategory !== 'all'
    const shouldAddGroupIdFilter = hasSearchQuery || isSpecificCategorySelected

    const { success, payload } = await searchService.fetchSearchProviderData({
      query: searchQuery,
      filters: selectedFilters,
      predefinedFilters,
      sort,
      page,
      resultsPerPage,
      destinationZone,
      selectedCategory,
      isPrintsPage,
      userSizePreference,
      shouldAddGroupIdFilter,
      defaultSortObject,
    })

    if (success) {
      const dataResponse = payload.data

      if (dataResponse.type === 'redirect') {
        if (isSafeRedirectUrl(dataResponse.redirectLink)) {
          window.open(dataResponse.redirectLink, '_blank', 'noopener,noreferrer')
        }
      }

      // setting artworks to redux
      if (dataResponse.type === 'products') {
        updateReduxStore({
          results: dataResponse.results,
          pagination: {
            resultsPerPage,
            pageNumber: page,
            totalResults: dataResponse.totalResults,
          },
        })
      }
    }

    setIsLoadingArtworks(false)
  }

  useEffect(() => {
    getSearchArtworksFromProvider()
  }, [page, resultsPerPage])

  return { artworksResults, isLoadingArtworks }
}

export default useSearchArtworks
